import { useNavigate } from 'react-router-dom';
import { Calendar, Eye, Clock, FolderOpen } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AssigneesDisplay } from '@/components/tasks/assignees-display';
import { formatDate, formatRelativeTime, getPriorityHexColor, getContrastTextColor } from '@/lib/utils';
import { useAuth } from '@/hooks/use-auth';
import type { TaskSearchResult } from '@/types';

interface SearchResultItemProps {
  task: TaskSearchResult;
}

export function SearchResultItem({ task }: SearchResultItemProps) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const isClient = user?.role === 'client';

  const handleClick = () => {
    navigate(`/tasks/${task.id}`);
  };

  const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && !task.completedAt;

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      }}
      className="p-4 hover:bg-accent/50 cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="font-medium text-foreground truncate">{task.title}</h3>
            {(() => {
              const bgColor = getPriorityHexColor(task.priority);
              const textColor = getContrastTextColor(bgColor);
              return (
                <Badge
                  variant="secondary"
                  className="capitalize font-medium"
                  style={{ backgroundColor: bgColor, color: textColor, borderColor: 'transparent' }}
                >
                  {task.priority}
                </Badge>
              );
            })()}
            {!isClient && task.isClientVisible && (
              <Badge variant="outline" className="gap-1 text-xs">
                <Eye className="h-3 w-3" />
                Client
              </Badge>
            )}
          </div>

          {task.description && (
            <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
              {task.description}
            </p>
          )}

          <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
            {(() => {
              const statusBgColor = task.status.color || '#6b7280';
              const statusTextColor = getContrastTextColor(statusBgColor);
              return (
                <Badge
                  variant="secondary"
                  className="font-medium"
                  style={{ backgroundColor: statusBgColor, color: statusTextColor, borderColor: 'transparent' }}
                >
                  {task.status.name}
                </Badge>
              );
            })()}

            {(task.assignees?.length ?? 0) > 0 && (
              <AssigneesDisplay assignees={task.assignees} variant="compact" className="max-w-[200px]" />
            )}

            {task.dueDate && (
              <span className={`inline-flex items-center gap-1 ${isOverdue ? 'text-destructive' : ''}`}>
                <Calendar className="h-3 w-3" />
                {formatDate(task.dueDate)}
                {isOverdue && <span className="font-medium">(Overdue)</span>}
              </span>
            )}
          </div>

          {/* Project/List and Last Updated */}
          <div className="flex items-center gap-4 mt-2 pt-2 border-t border-border/50 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <FolderOpen className="h-3 w-3" />
              <span
                className="inline-flex items-center gap-1"
                style={{ color: task.project?.color }}
              >
                <span
                  className="h-2 w-2 rounded-full shrink-0"
                  style={{ backgroundColor: task.project?.color }}
                />
                {task.project?.name || 'Unknown Project'}
              </span>
              <span className="text-muted-foreground/50">/</span>
              <span>{task.list?.name || 'Unknown List'}</span>
            </span>

            <span className="inline-flex items-center gap-1 ml-auto">
              <Clock className="h-3 w-3" />
              Updated {formatRelativeTime(task.updatedAt)}
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}
